import { createContext, useContext } from 'react';

/**
 * navNonce — ONE source for the browser-back ownership nonce. (Card P194.)
 *
 * ── ⛔⛔ WHY A CONTEXT AND NOT 20 PROPS ──────────────────────────────────────
 * useBrowserBackStep adopts an existing history marker only when it can prove
 * ownership (`mk.s === sessionNonce`), and its docblock states the
 * consequence: "An absent nonce adopts nothing — a hook that cannot prove
 * ownership pushes its own entry instead." Measured 31 Oct: of 23 call sites,
 * only App.js, CRMPage.js and (since #766) DrilldownModal.js passed one. The
 * other 20 pushed a history entry on every open that could never be reused —
 * the mechanism that closed the CEO drill-down modal.
 *
 * Nine of the twenty are TOP-LEVEL PAGES that already receive `sessionNonce`
 * from App.js:944 and simply never forward it. The other eleven are drawers,
 * panels and nested CRM tabs with no props path at all — ContactDrawer,
 * TranslationPanel, ManualSearchPanel, LeadIntakePanel, VoiceIntakeModal,
 * ContactWorkspaceNavStep, MedicalIntelligenceTab, ProprietaryVaultView,
 * HypothesesTab, EasyReports (not in App's page map). Threading a prop to
 * those means editing every component between them and the page.
 *
 * ⚠ TWO MECHANISMS FOR ONE VALUE IS HOW THEY DRIFT. Nine props plus eleven
 * threads is twenty chances to pass the wrong thing, and no way to tell from a
 * call site which it got. One context, read by the hook itself, means a call
 * site cannot get it wrong because it does not supply it.
 *
 * ── ⛔ WHY NOT DEFAULT TO currentLoginNonce() INSIDE THE HOOK ───────────────
 * That was the tempting one-liner and it is WRONG. App.js's nonce is
 * `${uid}.${seq}` (App.js:458); loginNonce's `currentLoginNonce()` reads a
 * stored key and returns a DIFFERENT string. Defaulting to it would mint
 * markers in a second namespace that App, CRMPage and DrilldownModal do not
 * share — so the three call sites that were already correct would stop
 * adopting each other's entries. The context carries App's OWN value, which
 * keeps every caller in one namespace by construction.
 *
 * ⚠ An explicit `sessionNonce` prop still WINS, so the three existing callers
 * are unchanged and a future caller can override deliberately.
 */
export const NavNonceContext = createContext('');

export function NavNonceProvider({ value = '', children }) {
  return <NavNonceContext.Provider value={value || ''}>{children}</NavNonceContext.Provider>;
}

/** The nonce in force, or '' when no provider is mounted (tests, storybook). */
export function useNavNonce() {
  return useContext(NavNonceContext) || '';
}
